import { CheckSquare, Plus, Trash } from '@phosphor-icons/react';

import { Banner } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { WizardStepHeader } from '../../wizard';
import { NumericInput } from '../../common/NumericInput';
import pt from '../../../locales/pt';
import { cn } from '../../../utils/cn';
import { formatEUR } from '../../../utils/money';
import { formatMonthLabel } from '../../../utils/goalWizardUtils';
import { goalAllocationShortLabel } from '../../../utils/goalDisplayUtils';

const ALLOCATION_VALUES = ['essential', 'lifestyle', 'personal_investment'];

/** Interpola `{token}` num template por valores em negrito, sem dangerouslySetInnerHTML. */
function interpolateBold(template, replacements) {
  const parts = [];
  let remaining = template;
  replacements.forEach(([token, value], index) => {
    const [before, after] = remaining.split(token);
    parts.push(before);
    parts.push(<b key={token + index}>{value}</b>);
    remaining = after ?? '';
  });
  parts.push(remaining);
  return parts;
}

export function SmartGoalEssentialStep({
  isChecklist,
  name,
  onNameChange,
  amount,
  onAmountChange,
  monthValue,
  onMonthChange,
  monthlyPreview,
  checklistTasks,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
  budgetAllocation,
  onBudgetAllocationChange,
}) {
  const copy = pt.goals.wizard.essential;
  const monthly = monthlyPreview?.monthly;
  const soft = monthlyPreview?.soft || { over: false };
  const cycles = monthlyPreview?.cycles;
  const showHero = !isChecklist && monthly != null && Number.isFinite(monthly);

  const unit =
    cycles === 1 ? copy.monthSingular : copy.monthPlural;

  const breakdown =
    showHero && cycles
      ? copy.monthlyBreakdown
          .replace('{amount}', formatEUR(monthlyPreview.targetAmount).replace(/\s*€$/, '').trim())
          .replace('{date}', formatMonthLabel(monthValue))
          .replace('{months}', String(cycles))
          .replace('{unit}', unit)
      : null;

  return (
    <div className="flex flex-col gap-3.5 pt-2">
      <WizardStepHeader
        eyebrow={copy.eyebrow}
        title={isChecklist ? copy.titleChecklist : copy.titleMonetary}
        subtitle={isChecklist ? copy.subtitleChecklist : copy.subtitleMonetary}
      />

      <div className="rounded-[14px] border-[1.5px] border-border px-[15px] py-2">
        <label className="block text-[11.5px] font-semibold text-muted-foreground">
          {copy.nameLabel}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={copy.namePlaceholder}
          className="w-full border-0 bg-transparent py-1 font-display text-lg font-semibold text-foreground outline-none placeholder:text-muted-foreground/50 focus:ring-0"
        />
      </div>

      {isChecklist ? (
        <div className="rounded-[14px] border-[1.5px] border-border px-3.5 py-2">
          <label className="block text-[11.5px] font-semibold text-muted-foreground">
            {copy.dateLabel}
          </label>
          <input
            type="month"
            value={monthValue}
            onChange={(e) => onMonthChange(e.target.value)}
            className="w-full border-0 bg-transparent py-1.5 font-display text-[15px] font-semibold text-foreground outline-none focus:ring-0"
          />
        </div>
      ) : (
        <div className="flex gap-2.5">
          <div className="flex-1 rounded-[14px] border-[1.5px] border-border px-3.5 py-2">
            <label className="block text-[11.5px] font-semibold text-muted-foreground">
              {copy.amountLabel}
            </label>
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-lg font-bold text-muted-foreground/60">€</span>
              <NumericInput
                value={amount}
                onChange={(e) => onAmountChange(e.target.value)}
                className="w-full min-w-0 border-0 bg-transparent p-0 font-display text-xl font-bold shadow-none focus-visible:ring-0"
              />
            </div>
          </div>
          <div className="flex-1 rounded-[14px] border-[1.5px] border-border px-3.5 py-2">
            <label className="block text-[11.5px] font-semibold text-muted-foreground">
              {copy.dateLabel}
            </label>
            <input
              type="month"
              value={monthValue}
              onChange={(e) => onMonthChange(e.target.value)}
              className="w-full border-0 bg-transparent py-1.5 font-display text-[15px] font-semibold text-foreground outline-none focus:ring-0"
            />
          </div>
        </div>
      )}

      {isChecklist ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-muted-foreground">{pt.goals.form.tasksLabel}</p>
          {checklistTasks.map((task, index) => (
            <div key={task.id || `new-${index}`} className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
              <Input
                value={task.title}
                placeholder={pt.goals.checklist.taskPlaceholder}
                onChange={(e) => onUpdateTask(index, e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                aria-label={pt.goals.checklist.removeTask}
                onClick={() => onRemoveTask(index)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="ghost" onClick={onAddTask}>
            <Plus className="mr-1 h-4 w-4" />
            {pt.goals.checklist.addTask}
          </Button>
        </div>
      ) : null}

      {!isChecklist ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-muted-foreground">
            {pt.goals.form.allocationLabel}
          </legend>
          <p className="text-xs text-muted-foreground">{pt.goals.form.allocationHelp}</p>
          <div className="flex flex-col gap-2">
            {ALLOCATION_VALUES.map((value) => {
              const selected = budgetAllocation === value;
              return (
                <label
                  key={value}
                  className={cn(
                    'flex cursor-pointer items-start gap-2.5 rounded-xl border px-3 py-2 transition-colors',
                    selected
                      ? 'border-sage-400 bg-sage-50/80 dark:border-sage-600 dark:bg-sage-900/50'
                      : 'border-border bg-card hover:border-border dark:hover:border-sage-700',
                  )}
                >
                  <input
                    type="radio"
                    name="budget_allocation"
                    value={value}
                    checked={selected}
                    onChange={() => onBudgetAllocationChange?.(value)}
                    className="mt-1"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                      {goalAllocationShortLabel(value, pt)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {pt.settings.annual.form.allocationHints[value]}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {showHero ? (
        <div className="rounded-[20px] bg-gradient-to-br from-sage-600 to-sage-800 px-[18px] py-[18px] text-center shadow-[0_16px_30px_-16px_rgba(74,98,67,0.7)]">
          <p className="text-xs font-semibold text-sage-100/90">{copy.monthlyLabel}</p>
          <p className="mt-1 font-display text-[36px] font-bold leading-none tracking-tight text-white tabular-nums">
            {formatEUR(monthly)}
          </p>
          {breakdown ? (
            <p className="mt-1 text-[12.5px] text-sage-100/80">{breakdown}</p>
          ) : null}
        </div>
      ) : null}

      {showHero && soft.remaining != null ? (
        soft.over ? (
          <Banner
            tone="warning"
            title={interpolateBold(copy.overBudget, [
              ['{budget}', formatEUR(soft.remaining)],
              ['{max}', formatEUR(soft.maxComfortableTarget)],
            ])}
          />
        ) : (
          <Banner
            tone="success"
            title={interpolateBold(copy.withinBudget, [
              ['{budget}', formatEUR(soft.remaining)],
            ])}
          />
        )
      ) : null}
    </div>
  );
}

export default SmartGoalEssentialStep;
